'use strict';

const cacheConfig = require('public/js/lib/cache-config');
const { removeItem } = require('public/js/lib/local-storage');
const localStorageSlim = require('localstorage-slim');
const LOCATION_SOURCE = require('public/js/lib/location-source');
const STORAGE_KEYS = require('public/js/lib/storage-keys');

/** Max length for postal/ZIP input in the shared location modal (header + PDP). */
const MODAL_POSTAL_CODE_MAX_LENGTH = 20;

/**
 * Rejection reason for {@link postLocationUpdate} (Error subtype for promise-rejection lint rules).
 */
class LocationUpdateRequestError extends Error {
	/**
	 * @param {number} status   HTTP status (0 if unknown)
	 * @param {string} errorCode API / transport code (e.g. `unsupported_zip`, `no_shipping_zone`, `http_400`, `request_failed`, or server `error` string)
	 */
	constructor(status, errorCode) {
		super(String(errorCode));
		this.name = 'LocationUpdateRequestError';
		this.status = status;
		this.errorCode = errorCode;
	}
}

/**
 * Shared ZIP/postal modal UI + POST /ajax/update-location (header + PDP).
 *
 * Keep behavior in one place so the PDP modal and header location module stay aligned.
 */

function clearModalZipError($modal) {
	$modal.find('.form-errors').text('');
	$modal.find('.zip-input').removeClass('is-invalid');
}

function showModalZipError($modal, errorMessage) {
	$modal.find('.form-errors').text(errorMessage);
	$modal.find('.zip-input').addClass('is-invalid');
}

function setZipSubmitButtonWorking($button, working) {
	if (working) {
		if ($button.prop('disabled')) {
			return;
		}
		$button.data('original-text', $button.html());
		$button.html('<i class="fa fa-spinner fa-pulse"></i>');
		$button.addClass('disabled').prop('disabled', true);
	} else {
		$button.html($button.data('original-text'));
		$button.removeClass('disabled').prop('disabled', false);
	}
}

/**
 * Clear prior errors, trim input, and validate length for the shared modal.
 *
 * @param {JQuery} $modal .location-modal root
 * @param {string|null|undefined} postalCodeRaw value from input or caller
 * @returns {string|null} trimmed postal when valid; null if invalid (error already shown)
 */
function getTrimmedModalPostalCodeOrShowError($modal, postalCodeRaw) {
	clearModalZipError($modal);
	const postalCode = (postalCodeRaw ?? '').trim();
	if (postalCode === '' || postalCode.length > MODAL_POSTAL_CODE_MAX_LENGTH) {
		showModalZipError($modal, $modal.find('.modal-content').data('error-invalid-zip'));
		return null;
	}
	return postalCode;
}

/**
 * @param {JQuery} $modal
 * @param {LocationUpdateRequestError} error
 */
function paintLocationUpdateErrorOnModal($modal, error) {
	if (!(error instanceof LocationUpdateRequestError)) {
		return;
	}
	const $modalContent = $modal.find('.modal-content');
	const errorMessages = {
		no_shipping_zone: $modalContent.data('error-no-shipping-zone'),
		unsupported_zip: $modalContent.data('error-invalid-zip'),
	};
	const genericErrorMessage = $modalContent.data('error-generic');
	if (error.status === 400) {
		const message =
			typeof error.errorCode === 'string' && Object.hasOwn(errorMessages, error.errorCode)
				? errorMessages[error.errorCode]
				: errorMessages.unsupported_zip;
		showModalZipError($modal, message);

		return;
	}
	showModalZipError($modal, genericErrorMessage);
}

/**
 * POST `/ajax/update-location`. When `$modal` is passed, 4xx / transport failures paint
 * `.modal-content` errors synchronously in the jQuery callback (same timing as an inline handler)
 * before the returned Promise rejects. Omit `$modal` for UI-agnostic callers (e.g. find-dealer).
 *
 * @param {{ postalCode: string, customerId?: string, warehouseId?: string }} payload
 * @param {JQuery|null} [$modal] optional `.location-modal` root for inline error display
 * @returns {Promise<object>} resolves with response JSON; rejects with {@link LocationUpdateRequestError}
 */
function postLocationUpdate(payload, $modal = null) {
	return new Promise((resolve, reject) => {
		globalThis.rp_app.request({
			method: 'POST',
			url: '/ajax/update-location',
			dataType: 'json',
			data: payload,
			success: (data = {}) => {
				resolve(data);
			},
			statusCode: {
				400: (jqxhr) => {
					const data = jqxhr.responseJSON || {};
					const rawError = data.error;
					const errorCode = typeof rawError === 'string' && rawError !== ''
						? rawError
						: 'http_400';
					const error = new LocationUpdateRequestError(400, errorCode);
					if ($modal) {
						paintLocationUpdateErrorOnModal($modal, error);
					}
					reject(error);
				}
			},
			error: function (jqxhr) {
				if (this.statusCode?.[jqxhr.status]) {
					return;
				}
				const error = new LocationUpdateRequestError(jqxhr.status || 0, 'request_failed');
				if ($modal) {
					paintLocationUpdateErrorOnModal($modal, error);
				}
				reject(error);
			}
		});
	});
}

/**
 * POST manual ZIP to session (same as header modal). Resolves with JSON body on 2xx.
 * Optional `customerId` / `warehouseId` enable PDP-only `pdpShipping` in the same response.
 * Same transport as {@link postLocationUpdate}; passes `$modal` so errors paint inline.
 *
 * @param {{ postalCode: string, customerId?: string, warehouseId?: string }} payload
 * @param {JQuery} $modal .location-modal root
 * @returns {Promise<object>} rejects with {@link LocationUpdateRequestError}
 */
function postUpdateLocation(payload, $modal) {
	return postLocationUpdate(payload, $modal);
}

/**
 * After successful update-location, mirror header modal: cache + supplier key (lead-gen UX).
 *
 * @param {string} displayPostal
 * @param {object} location Location payload from API (normalized values)
 */
function persistManualLocationAfterUpdate(displayPostal, location) {
	localStorageSlim.set(
		cacheConfig.LOCATION_CACHE_KEY,
		{
			postalCode: displayPostal,
			location,
			source: LOCATION_SOURCE.MANUAL,
		},
		{ttl: cacheConfig.getTtlFor('location')}
	);
	removeItem(STORAGE_KEYS.SUPPLIER_V1);
}

/**
 * Header + PDP: POST `/ajax/update-location`, persist cache, then full reload on success.
 * Callers own any client-side validation beyond server-side checks; this path assumes the
 * POST is allowed to run (modal errors come from `postUpdateLocation` on 4xx).
 *
 * @param {{ postalCode: string }} payload
 * @param {JQuery} $modal
 * @param {JQuery} $submitButton
 * @param {(displayPostal: string, location: object) => void} [onBeforeReload] runs before reload if provided
 * @returns {Promise<void>}
 */
function submitManualZipAndReload(payload, $modal, $submitButton, onBeforeReload) {
	setZipSubmitButtonWorking($submitButton, true);
	return postUpdateLocation(payload, $modal)
		.then((data) => {
			if (!data?.location) {
				setZipSubmitButtonWorking($submitButton, false);
				return;
			}
			const location = data.location;
			const displayPostal = String(
				location.postal_code ?? location.postalCode ?? payload.postalCode
			).trim();
			if (typeof onBeforeReload === 'function') {
				onBeforeReload(displayPostal, location);
			}
			persistManualLocationAfterUpdate(displayPostal, location);
			globalThis.location.reload();
		})
		.catch(() => {
			setZipSubmitButtonWorking($submitButton, false);
		});
}

module.exports = {
	LocationUpdateRequestError,
	MODAL_POSTAL_CODE_MAX_LENGTH,
	clearModalZipError,
	getTrimmedModalPostalCodeOrShowError,
	persistManualLocationAfterUpdate,
	postLocationUpdate,
	postUpdateLocation,
	setZipSubmitButtonWorking,
	showModalZipError,
	submitManualZipAndReload,
};
